import * as ActionType from './types'
import { hideAPIErrorInfo, showAPIErrorInfo } from './errorActions'
import {
  getCompareGeneralData,
  getDetailsData,
} from 'settings/xhr/axiosRequests'
import { entryPointId } from 'settings/app/constants'
import { ACCORDION_ORDER } from '../../containers/Detail/partials/'
import {
  getApiEndpointByEntrypoint,
  mergeArraysWithoutDuplications,
} from 'settings/app/helpers'
import { getSearchUrl } from 'settings/app/urlHelpers'
import {
  RESULT_HAS_COMPARE_DATA,
  RESULT_HAS_CONTRIBUTION,
  SKIP_DETAIL_KEY_FOR_COMPARE,
} from 'containers/Compare/helpers/constants'

/**
 * first compare request
 * get bookmarked items general data
 * @param type
 * @param bookmarkIds
 * @param isMobile
 * @param reloadWithOutTreatment
 * @returns {(function(*, *): Promise<void>)|*}
 */
export const getCompareItems =
  ({ type, bookmarkIds, isMobile, reloadWithOutTreatment = false }) =>
  async (dispatch, getState) => {
    if (!!getState().apiError) {
      dispatch(hideAPIErrorInfo())
    }
    const { searchParams } = getState().bookmarks.types[type]
    const compareState = getState().compare.types[type]

    await getCompareGeneralData({
      type,
      ids: bookmarkIds,
      searchParams,
      reloadWithOutTreatment,
    })
      .then((data) => {
        let activeElements

        // new search
        // on mobile device all details will load to avoid re rendering or desktop max three items
        // else reload without treatment params (hold active elements)
        if (!reloadWithOutTreatment) {
          if (!isMobile) {
            activeElements = data.results.filter((result, index) =>
              index <= 2 ? result.id : false
            )
          } else {
            activeElements = data.results.filter((result) => result.id)
          }
        } else {
          // merge previous loaded details into result state
          try {
            activeElements = getState().compare.activeElements.activeIds
            data.results.forEach((result) => {
              // skip not active results and add detail prop is missing
              if (!activeElements.includes(result.id)) {
                if (!result?.details) {
                  result.details = {}
                }
              }

              const resultStateInStore = compareState.results.find(
                (resultStore) => resultStore.id === result.id
              )
              result.details = resultStateInStore.details
            })
          } catch (e) {
            throw new Error(
              `Error in fn.getCompareItems (merge result state): MSG => ${ e }`
            )
          }
        }

        // get compare keys (required for comparing values)
        if (RESULT_HAS_COMPARE_DATA.includes(type)) {
          // case hospital, get qsr keys for compare
          if (type === entryPointId.HOSPITALS) {
            data.qsrIdentifier =
              data.is_treatment_reason && !reloadWithOutTreatment
                ? getQsrIdentifierArray(activeElements)
                : []
          }
          data.compareKeys = getCompareKeys(data.results, type)
        }

        // set initial data
        dispatch({
          type: ActionType.FETCH_COMPARE_ITEMS_COMPLETE,
          payload: {
            resultTyp: type,
            data,
            activeElements: reloadWithOutTreatment
              ? activeElements
              : activeElements.map((result) => result.id), // ids from the first three elements
          },
        })
      })
      .catch((e) => {
        dispatch(showAPIErrorInfo())
        console.error('fetch details error: ', e)
      })
  }

/**
 * load details data
 * 1. step get all ids from given activeIds which are not loaded before
 * 2. load associated data from this ids
 * 3. merge new ids into redux state
 * @param activeIds
 * @param compareWithOutSearchTerm
 * @returns {(function(*, *): Promise<void>)|*}
 */
export const getAllDetails =
  (activeIds, compareWithOutSearchTerm) => async (dispatch, getState) => {
    if (!!getState().apiError) {
      dispatch(hideAPIErrorInfo())
    }

    const selectedType = getState().compare.activeElements.type
    const bookmarksState = getState().bookmarks.types[selectedType]
    const compareState = getState().compare.types[selectedType]
    const { details: detailIds } = getState().compare.xhr[selectedType]
    const endpoint = getApiEndpointByEntrypoint(selectedType)
    let queryParams

    // get the id from the elements which are not loaded before
    // else hold active elements
    let loadIds
    if (!compareWithOutSearchTerm) {
      loadIds = activeIds.filter((x) => !detailIds.includes(x))
    } else {
      loadIds = activeIds
    }

    if (loadIds.length > 0) {
      const promiseArray = loadIds.map(async (id) => {
        // get current result from redux
        const currentCompareObject = compareState.results.find(
          (result) => result.id === id
        )

        queryParams = {
          city: currentCompareObject.address.city,
          zip: currentCompareObject.address.zip,
        }

        // append treatment params (hospital only)
        if (
          !compareWithOutSearchTerm &&
          selectedType === entryPointId.HOSPITALS
        ) {
          queryParams.key = bookmarksState.key
          queryParams.type = bookmarksState.type
          queryParams.query = bookmarksState.query
        }

        const data = await getDetailsData({
          endpoint,
          id,
          queryParams: getSearchUrl(queryParams),
        })

        // request wasn't successfully
        // TODO: api error handling could be improve
        if (!data?.result) {
          return { id, details: [] }
        }

        return { id, details: data.result.details }
      })

      const newCompareStateResult = [...compareState.results]

      /**
       * execute array of promises (fetching is done)
       * data mapping, reorder detail keys
       */
      Promise.all(promiseArray)
        .then((newCompareObjects) => {
          let detailOrder // details order array
          const detailsKeyArray = [] // get all details props in correct order

          // accordion order
          if (selectedType === entryPointId.HOSPITALS) {
            if (getState().compare.types[selectedType].isTreatmentReason) {
              detailOrder = ACCORDION_ORDER.HOSPITAL_WITH_TREATMENT_REASON
            } else {
              detailOrder = ACCORDION_ORDER.HOSPITAL_WITH_OUT_TREATMENT_REASON
            }
          } else {
            detailOrder = ACCORDION_ORDER[selectedType.toUpperCase()]
          }

          // sort details Keys array
          detailOrder.forEach((detailPropKey) => {
            // skip images (for booth data cases)
            if (SKIP_DETAIL_KEY_FOR_COMPARE.includes(detailPropKey)) return true

            newCompareObjects.forEach((compObject) => {
              if (
                // if key where not added before and object hast key data then add
                !detailsKeyArray.includes(detailPropKey) &&
                compObject.details.hasOwnProperty(detailPropKey) &&
                compObject.details[detailPropKey] !== null &&
                compObject.details[detailPropKey].length > 0
              ) {
                detailsKeyArray.push(detailPropKey)
              }
            })
          })

          // sort results (rendering based on sorted result data)
          newCompareObjects.forEach((compObject) => {
            const resultIndex = newCompareStateResult.indexOf(
              compareState.results.find((result) => result.id === compObject.id)
            )
            newCompareStateResult[resultIndex].details = {
              ...compObject.details,
            }
          })

          return { newCompareStateResult, detailsKeyArray }
        })
        .then(({ newCompareStateResult, detailsKeyArray }) => {
          // merge new ids into current ids
          const details = mergeArraysWithoutDuplications(
            activeIds,
            getState().compare.xhr[selectedType].details
          )

          // avoid showing no detail information when no detail keys found
          if (detailsKeyArray.length === 0) {
            detailsKeyArray = compareState.detailsKeyArray
          }

          dispatch({
            type: ActionType.FETCH_COMPARE_DETAILS_COMPLETE,
            payload: {
              resultTyp: selectedType,
              results: newCompareStateResult,
              details,
              detailsKeyArray,
            },
          })
        })
        .catch((error) => {
          // handle error
          console.error('fetch details error: ', error)
          dispatch(showAPIErrorInfo())
        })
    }
  }

/**
 * set the active items
 * @param activeElements
 * @param qsrIdentifier
 * @returns {{payload: {activeElements: *}, type: string}}
 */
export const setActiveElements = (activeElements, qsrIdentifier = false) => {
  if (activeElements.type === entryPointId.HOSPITALS) {
    return {
      type: ActionType.TOGGLE_ACTIVE_COMPARE_ITEMS,
      payload: { activeElements, qsrIdentifier },
    }
  }

  return {
    type: ActionType.TOGGLE_ACTIVE_COMPARE_ITEMS,
    payload: { activeElements },
  }
}

/**
 * reset to initial state
 * @returns {{type: string}}
 */
export const resetCompareState = () => ({
  type: ActionType.RESET_COMPARE_STATE,
})

/**
 * get all qsr identifier from a result collection
 * @param objectCollection
 * @returns {[]}
 */
export const getQsrIdentifierArray = (objectCollection) => {
  const qsrArray = []
  // parse all results
  objectCollection.forEach((object) => {
    if (object.hasOwnProperty('qsr') && object.qsr !== null) {
      object.qsr.forEach((qsrValue) => {
        // search if identifier still exists in array
        if (
          qsrArray.filter(
            (qsrIdentifier) => qsrIdentifier.identifier === qsrValue.identifier
          ).length === 0
        ) {
          qsrArray.push({
            identifier: qsrValue.identifier,
            title: qsrValue.title,
          })
        }
      })
    }
  })

  return qsrArray
}

/**
 * get all non qsr identifier from a result collection
 * @param objectCollection
 * @param type
 * @returns {[]}
 */
export const getCompareKeys = (objectCollection, type) => {
  const compareKeys = []

  // console.log('RESULT => ', objectCollection)
  // console.log('type => ', type)
  // console.log({ resultHasContribution })

  objectCollection.forEach((result) => {
    // console.log('result => ', result)

    if (type === entryPointId.HOSPITALS) {
      if (result?.peq?.general !== null && !compareKeys.includes('general')) {
        compareKeys.push('general')
      }
      if (
        result?.treatment !== null &&
        result.treatment.cases !== null &&
        !compareKeys.includes('treatmentCases')
      ) {
        compareKeys.push('treatment')
        // rename prop to avoid compare bugs (response data includes two cases props)
        result.treatment.treatmentCases = result.treatment.cases
      }
      if (result?.iqtig !== null && !compareKeys.includes('iqtig')) {
        compareKeys.push('iqtig')
        result.iqtig = parseInt(result.iqtig, 10)
      }
    } else if (RESULT_HAS_CONTRIBUTION.includes(type)) {
      // different contributions
      if (
        result?.contribution &&
        Array.isArray(result.contribution) &&
        result.contribution.length > 0 &&
        !compareKeys.includes('contribution')
      ) {
        compareKeys.push('contribution')
      }
    }

    // transparent report (careservices)
    if (
      result?.transparencyReport &&
      typeof result.transparencyReport === 'string' &&
      result.transparencyReport.trim() !== '' &&
      !compareKeys.includes('transparencyReport')
    ) {
      compareKeys.push('transparencyReport')
    }

    // pricePerHour (caresupport)
    if (
      result?.pricePerHour &&
      typeof result.pricePerHour === 'string' &&
      result.pricePerHour.trim() !== '' &&
      !compareKeys.includes('pricePerHour')
    ) {
      compareKeys.push('pricePerHour')
    }

    if (
      result?.services &&
      Array.isArray(result.services) &&
      result.services.length > 0 &&
      !compareKeys.includes('services')
    ) {
      compareKeys.push('services')
    }
  })

  return compareKeys
}
